/**
 * Editable cell for macro values (calories, protein, carbs, fat).
 * Click to edit, blur/enter to save, escape to cancel.
 */

import * as stylex from "@stylexjs/stylex";
import { useCallback, useEffect, useRef, useState } from "react";

import { table } from "@animus/primaris";

import { styles } from "./styles.stylex.ts";

const cellStyles = stylex.create({
  editableCell: {
    cursor: "pointer",
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.05)",
    },
  },
  valueButton: {
    backgroundColor: "transparent",
    borderWidth: 0,
    padding: 0,
    margin: 0,
    fontFamily: "monospace",
    fontSize: 13,
    color: "inherit",
    cursor: "pointer",
    ":focus": {
      outline: "none",
    },
  },
  input: {
    width: 60,
    fontFamily: "monospace",
    fontSize: 13,
    paddingBlock: 2,
    paddingInline: 4,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "rgba(255,255,255,0.2)",
    borderRadius: 4,
    backgroundColor: "rgba(0,0,0,0.2)",
    color: "inherit",
    ":focus": {
      outline: "none",
      borderColor: "rgba(255,255,255,0.4)",
    },
  },
});

interface EditableMacroCellProps {
  value: number;
  suffix?: string;
  onSave: (value: number) => void;
}

function EditableMacroCell({ value, suffix, onSave }: EditableMacroCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = useCallback(() => {
    setEditValue(String(value));
    setIsEditing(true);
  }, [value]);

  const handleSave = useCallback(() => {
    const parsed = Number.parseFloat(editValue);
    if (!Number.isNaN(parsed) && parsed >= 0 && parsed !== value) {
      onSave(parsed);
    }
    setIsEditing(false);
  }, [editValue, value, onSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSave();
      } else if (e.key === "Escape") {
        setIsEditing(false);
      }
    },
    [handleSave]
  );

  if (isEditing) {
    return (
      <td {...stylex.props(table.td, styles.macroCell)}>
        <input
          ref={inputRef}
          {...stylex.props(cellStyles.input)}
          min={0}
          onBlur={handleSave}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          step="any"
          type="number"
          value={editValue}
        />
      </td>
    );
  }

  return (
    <td {...stylex.props(table.td, styles.macroCell, cellStyles.editableCell)}>
      <button
        {...stylex.props(cellStyles.valueButton)}
        onClick={handleClick}
        type="button"
      >
        {value}
        {suffix}
      </button>
    </td>
  );
}

export { EditableMacroCell, type EditableMacroCellProps };
